import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { configApi, transactionApi, accountApi } from '../services/api';

interface ReasonItem { id: number; reason: string; }
interface SectorItem { id: number; sector: string; }
interface IndustryItem { id: number; name: string; cost: number; balance: number; cap: number; comment: string | null; }

interface StocklistItem { id: number; cik: string; exchange: string; ticker: string; name: string; shares: number; cusip: string; }
interface MissingStock { ticker: string; cik: string; exchange: string; name: string; }
interface StockSymbolItem { id: number; exchange: string; ticker: string; name: string; }
interface MissingCanadian { exchange: string; ticker: string; name: string; }

interface TrackedFilerItem { id: number; cik: string; filerName: string; displayName: string; active: number; }
interface AccountItem { id: number; account: string; comment: string | null; active: boolean; }

type TabKey = 'reasons' | 'sectors' | 'industries' | 'accounts' | 'stocklists' | 'missing' | 'missingCA' | 'avgCost' | 'avgSell' | 'trackedFilers';

/* ---- Reasons Tab ---- */
const ReasonsTab: React.FC = () => {
  const [items, setItems] = useState<ReasonItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState<number | null>(null);
  const [editVal, setEditVal] = useState('');
  const [newVal, setNewVal] = useState('');
  const [showAdd, setShowAdd] = useState(false);

  const load = () => {
    setLoading(true);
    configApi.reasons().then(r => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const startEdit = (item: ReasonItem) => { setEditId(item.id); setEditVal(item.reason); };
  const cancelEdit = () => { setEditId(null); setEditVal(''); };
  const saveEdit = async () => {
    if (editId === null) return;
    await configApi.updateReason(editId, { reason: editVal });
    cancelEdit();
    load();
  };
  const handleAdd = async () => {
    if (!newVal.trim()) return;
    await configApi.createReason({ reason: newVal.trim() });
    setNewVal('');
    setShowAdd(false);
    load();
  };
  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this reason?')) return;
    await configApi.deleteReason(id);
    load();
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '2em' }}>Loading...</div>;

  return (
    <>
      <div style={{ marginBottom: '0.5em' }}>
        <input type="button" value="Add Reason" onClick={() => setShowAdd(true)} style={styles.button} />
      </div>
      {showAdd && (
        <div style={styles.addRow}>
          <label style={styles.addLabel}>Reason:</label>
          <input value={newVal} onChange={e => setNewVal(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()} style={{ ...styles.input, width: '20em' }} autoFocus />
          <input type="button" value="Save" onClick={handleAdd} style={{ ...styles.button, marginLeft: '0.5em' }} />
          <input type="button" value="Cancel" onClick={() => { setShowAdd(false); setNewVal(''); }} style={{ ...styles.button, marginLeft: '4px' }} />
        </div>
      )}
      <table style={{ ...styles.table, width: '50%' }}>
        <thead>
          <tr>
            <th style={styles.th}>ID</th>
            <th style={styles.th}>Reason</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r, idx) => (
            <tr key={r.id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#e6f2ff' }}>
              <td style={{ ...styles.td, textAlign: 'center', width: '3em' }}>{r.id}</td>
              <td style={styles.td}>
                {editId === r.id ? (
                  <input value={editVal} onChange={e => setEditVal(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
                    style={{ ...styles.input, width: '100%' }} autoFocus />
                ) : r.reason}
              </td>
              <td style={{ ...styles.td, textAlign: 'center', width: '10em' }}>
                {editId === r.id ? (
                  <>
                    <a href="#s" onClick={e => { e.preventDefault(); saveEdit(); }} style={styles.actionLink}>Save</a>
                    {' | '}
                    <a href="#c" onClick={e => { e.preventDefault(); cancelEdit(); }} style={styles.actionLink}>Cancel</a>
                  </>
                ) : (
                  <>
                    <a href="#e" onClick={e => { e.preventDefault(); startEdit(r); }} style={styles.actionLink}>Edit</a>
                    {' | '}
                    <a href="#d" onClick={e => { e.preventDefault(); handleDelete(r.id); }} style={styles.actionLink}>Delete</a>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

/* ---- Sectors Tab ---- */
const SectorsTab: React.FC = () => {
  const [items, setItems] = useState<SectorItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState<number | null>(null);
  const [editVal, setEditVal] = useState('');
  const [newVal, setNewVal] = useState('');
  const [showAdd, setShowAdd] = useState(false);

  const load = () => {
    setLoading(true);
    configApi.sectors().then(r => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const startEdit = (item: SectorItem) => { setEditId(item.id); setEditVal(item.sector); };
  const cancelEdit = () => { setEditId(null); setEditVal(''); };
  const saveEdit = async () => {
    if (editId === null) return;
    await configApi.updateSector(editId, { sector: editVal });
    cancelEdit();
    load();
  };
  const handleAdd = async () => {
    if (!newVal.trim()) return;
    await configApi.createSector({ sector: newVal.trim() });
    setNewVal('');
    setShowAdd(false);
    load();
  };
  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this sector?')) return;
    await configApi.deleteSector(id);
    load();
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '2em' }}>Loading...</div>;

  return (
    <>
      <div style={{ marginBottom: '0.5em' }}>
        <input type="button" value="Add Sector" onClick={() => setShowAdd(true)} style={styles.button} />
      </div>
      {showAdd && (
        <div style={styles.addRow}>
          <label style={styles.addLabel}>Sector:</label>
          <input value={newVal} onChange={e => setNewVal(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()} style={{ ...styles.input, width: '20em' }} autoFocus />
          <input type="button" value="Save" onClick={handleAdd} style={{ ...styles.button, marginLeft: '0.5em' }} />
          <input type="button" value="Cancel" onClick={() => { setShowAdd(false); setNewVal(''); }} style={{ ...styles.button, marginLeft: '4px' }} />
        </div>
      )}
      <table style={{ ...styles.table, width: '50%' }}>
        <thead>
          <tr>
            <th style={styles.th}>ID</th>
            <th style={styles.th}>Sector</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((s, idx) => (
            <tr key={s.id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#e6f2ff' }}>
              <td style={{ ...styles.td, textAlign: 'center', width: '3em' }}>{s.id}</td>
              <td style={styles.td}>
                {editId === s.id ? (
                  <input value={editVal} onChange={e => setEditVal(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
                    style={{ ...styles.input, width: '100%' }} autoFocus />
                ) : s.sector}
              </td>
              <td style={{ ...styles.td, textAlign: 'center', width: '10em' }}>
                {editId === s.id ? (
                  <>
                    <a href="#s" onClick={e => { e.preventDefault(); saveEdit(); }} style={styles.actionLink}>Save</a>
                    {' | '}
                    <a href="#c" onClick={e => { e.preventDefault(); cancelEdit(); }} style={styles.actionLink}>Cancel</a>
                  </>
                ) : (
                  <>
                    <a href="#e" onClick={e => { e.preventDefault(); startEdit(s); }} style={styles.actionLink}>Edit</a>
                    {' | '}
                    <a href="#d" onClick={e => { e.preventDefault(); handleDelete(s.id); }} style={styles.actionLink}>Delete</a>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

/* ---- Industries Tab ---- */
const IndustriesTab: React.FC = () => {
  const [items, setItems] = useState<IndustryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ name: '', cost: 0, balance: 0, cap: 0, comment: '' });
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', cost: 0, balance: 0, cap: 0, comment: '' });

  const load = () => {
    setLoading(true);
    configApi.industries().then(r => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const startEdit = (item: IndustryItem) => {
    setEditId(item.id);
    setEditForm({ name: item.name, cost: item.cost, balance: item.balance, cap: item.cap, comment: item.comment || '' });
  };
  const cancelEdit = () => { setEditId(null); };
  const saveEdit = async () => {
    if (editId === null) return;
    await configApi.updateIndustry(editId, editForm);
    cancelEdit();
    load();
  };
  const handleAdd = async () => {
    if (!addForm.name.trim()) return;
    await configApi.createIndustry(addForm);
    setAddForm({ name: '', cost: 0, balance: 0, cap: 0, comment: '' });
    setShowAdd(false);
    load();
  };
  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this industry?')) return;
    await configApi.deleteIndustry(id);
    load();
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '2em' }}>Loading...</div>;

  return (
    <>
      <div style={{ marginBottom: '0.5em' }}>
        <input type="button" value="Add Industry" onClick={() => setShowAdd(true)} style={styles.button} />
      </div>
      {showAdd && (
        <div style={styles.addRow}>
          <label style={styles.addLabel}>Name:</label>
          <input value={addForm.name} onChange={e => setAddForm({ ...addForm, name: e.target.value })} style={{ ...styles.input, width: '12em' }} autoFocus />
          <label style={{ ...styles.addLabel, marginLeft: '0.5em' }}>Cost:</label>
          <input type="number" step="0.01" value={addForm.cost} onChange={e => setAddForm({ ...addForm, cost: parseFloat(e.target.value) || 0 })} style={{ ...styles.input, width: '8em' }} />
          <label style={{ ...styles.addLabel, marginLeft: '0.5em' }}>Balance:</label>
          <input type="number" step="0.01" value={addForm.balance} onChange={e => setAddForm({ ...addForm, balance: parseFloat(e.target.value) || 0 })} style={{ ...styles.input, width: '8em' }} />
          <label style={{ ...styles.addLabel, marginLeft: '0.5em' }}>Cap:</label>
          <input type="number" step="0.01" value={addForm.cap} onChange={e => setAddForm({ ...addForm, cap: parseFloat(e.target.value) || 0 })} style={{ ...styles.input, width: '8em' }} />
          <label style={{ ...styles.addLabel, marginLeft: '0.5em' }}>Comment:</label>
          <input value={addForm.comment} onChange={e => setAddForm({ ...addForm, comment: e.target.value })} style={{ ...styles.input, width: '14em' }} />
          <input type="button" value="Save" onClick={handleAdd} style={{ ...styles.button, marginLeft: '0.5em' }} />
          <input type="button" value="Cancel" onClick={() => { setShowAdd(false); setAddForm({ name: '', cost: 0, balance: 0, cap: 0, comment: '' }); }} style={{ ...styles.button, marginLeft: '4px' }} />
        </div>
      )}
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>ID</th>
            <th style={styles.th}>Name</th>
            <th style={styles.th}>Cost</th>
            <th style={styles.th}>Balance</th>
            <th style={styles.th}>Cap</th>
            <th style={styles.th}>Comment</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((ind, idx) => (
            <tr key={ind.id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#e6f2ff' }}>
              <td style={{ ...styles.td, textAlign: 'center', width: '3em' }}>{ind.id}</td>
              <td style={styles.td}>
                {editId === ind.id ? (
                  <input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} style={{ ...styles.input, width: '100%' }} autoFocus />
                ) : ind.name}
              </td>
              <td style={{ ...styles.td, textAlign: 'right' }}>
                {editId === ind.id ? (
                  <input type="number" step="0.01" value={editForm.cost} onChange={e => setEditForm({ ...editForm, cost: parseFloat(e.target.value) || 0 })} style={{ ...styles.input, width: '7em' }} />
                ) : ind.cost.toFixed(2)}
              </td>
              <td style={{ ...styles.td, textAlign: 'right' }}>
                {editId === ind.id ? (
                  <input type="number" step="0.01" value={editForm.balance} onChange={e => setEditForm({ ...editForm, balance: parseFloat(e.target.value) || 0 })} style={{ ...styles.input, width: '7em' }} />
                ) : ind.balance.toFixed(2)}
              </td>
              <td style={{ ...styles.td, textAlign: 'right' }}>
                {editId === ind.id ? (
                  <input type="number" step="0.01" value={editForm.cap} onChange={e => setEditForm({ ...editForm, cap: parseFloat(e.target.value) || 0 })} style={{ ...styles.input, width: '7em' }} />
                ) : ind.cap.toFixed(2)}
              </td>
              <td style={styles.td}>
                {editId === ind.id ? (
                  <input value={editForm.comment} onChange={e => setEditForm({ ...editForm, comment: e.target.value })} style={{ ...styles.input, width: '100%' }} />
                ) : (ind.comment || '-')}
              </td>
              <td style={{ ...styles.td, textAlign: 'center', width: '10em' }}>
                {editId === ind.id ? (
                  <>
                    <a href="#s" onClick={e => { e.preventDefault(); saveEdit(); }} style={styles.actionLink}>Save</a>
                    {' | '}
                    <a href="#c" onClick={e => { e.preventDefault(); cancelEdit(); }} style={styles.actionLink}>Cancel</a>
                  </>
                ) : (
                  <>
                    <a href="#e" onClick={e => { e.preventDefault(); startEdit(ind); }} style={styles.actionLink}>Edit</a>
                    {' | '}
                    <a href="#d" onClick={e => { e.preventDefault(); handleDelete(ind.id); }} style={styles.actionLink}>Delete</a>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

/* ---- Accounts Tab ---- */
const AccountsTab: React.FC = () => {
  const [items, setItems] = useState<AccountItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ account: '', comment: '', active: true });
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ account: '', comment: '', active: true });

  const load = () => {
    setLoading(true);
    accountApi.list().then(r => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const startEdit = (item: AccountItem) => {
    setEditId(item.id);
    setEditForm({ account: item.account, comment: item.comment || '', active: item.active });
  };
  const cancelEdit = () => { setEditId(null); };
  const saveEdit = async () => {
    if (editId === null) return;
    await accountApi.update(editId, editForm);
    cancelEdit();
    load();
  };
  const handleAdd = async () => {
    if (!addForm.account.trim()) return;
    await accountApi.create(addForm);
    setAddForm({ account: '', comment: '', active: true });
    setShowAdd(false);
    load();
  };
  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this account?')) return;
    await accountApi.delete(id);
    load();
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '2em' }}>Loading...</div>;

  return (
    <>
      <div style={{ marginBottom: '0.5em' }}>
        <input type="button" value="Add Account" onClick={() => setShowAdd(true)} style={styles.button} />
      </div>
      {showAdd && (
        <div style={styles.addRow}>
          <label style={styles.addLabel}>Account:</label>
          <input value={addForm.account} onChange={e => setAddForm({ ...addForm, account: e.target.value })}
            onKeyDown={e => e.key === 'Enter' && handleAdd()} style={{ ...styles.input, width: '14em' }} autoFocus />
          <label style={{ ...styles.addLabel, marginLeft: '0.5em' }}>Comment:</label>
          <input value={addForm.comment} onChange={e => setAddForm({ ...addForm, comment: e.target.value })}
            onKeyDown={e => e.key === 'Enter' && handleAdd()} style={{ ...styles.input, width: '20em' }} />
          <label style={{ ...styles.addLabel, marginLeft: '0.5em', display: 'flex', alignItems: 'center', gap: '3px' }}>
            <input type="checkbox" checked={addForm.active} onChange={e => setAddForm({ ...addForm, active: e.target.checked })} /> Active
          </label>
          <input type="button" value="Save" onClick={handleAdd} style={{ ...styles.button, marginLeft: '0.5em' }} />
          <input type="button" value="Cancel" onClick={() => { setShowAdd(false); setAddForm({ account: '', comment: '', active: true }); }} style={{ ...styles.button, marginLeft: '4px' }} />
        </div>
      )}
      <table style={{ ...styles.table, width: '70%' }}>
        <thead>
          <tr>
            <th style={styles.th}>ID</th>
            <th style={styles.th}>Account</th>
            <th style={styles.th}>Comment</th>
            <th style={styles.th}>Active</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((a, idx) => (
            <tr key={a.id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#e6f2ff', opacity: a.active ? 1 : 0.5 }}>
              <td style={{ ...styles.td, textAlign: 'center', width: '3em' }}>{a.id}</td>
              <td style={styles.td}>
                {editId === a.id ? (
                  <input value={editForm.account} onChange={e => setEditForm({ ...editForm, account: e.target.value })}
                    onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
                    style={{ ...styles.input, width: '100%' }} autoFocus />
                ) : <strong>{a.account}</strong>}
              </td>
              <td style={styles.td}>
                {editId === a.id ? (
                  <input value={editForm.comment} onChange={e => setEditForm({ ...editForm, comment: e.target.value })}
                    onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
                    style={{ ...styles.input, width: '100%' }} />
                ) : (a.comment || '-')}
              </td>
              <td style={{ ...styles.td, textAlign: 'center', width: '5em' }}>
                {editId === a.id ? (
                  <input type="checkbox" checked={editForm.active} onChange={e => setEditForm({ ...editForm, active: e.target.checked })} />
                ) : (a.active ? 'Y' : 'N')}
              </td>
              <td style={{ ...styles.td, textAlign: 'center', width: '10em' }}>
                {editId === a.id ? (
                  <>
                    <a href="#s" onClick={e => { e.preventDefault(); saveEdit(); }} style={styles.actionLink}>Save</a>
                    {' | '}
                    <a href="#c" onClick={e => { e.preventDefault(); cancelEdit(); }} style={styles.actionLink}>Cancel</a>
                  </>
                ) : (
                  <>
                    <a href="#e" onClick={e => { e.preventDefault(); startEdit(a); }} style={styles.actionLink}>Edit</a>
                    {' | '}
                    <a href="#d" onClick={e => { e.preventDefault(); handleDelete(a.id); }} style={styles.actionLink}>Delete</a>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

/* ---- Stock Lists Tab ---- */
const EXCHANGES = ['NYSE', 'NASDAQ', 'NYSEARCA', 'NYSEAMEX', 'BATS', 'OTC'];

const StocklistsTab: React.FC = () => {
  const [items, setItems] = useState<StocklistItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [editId, setEditId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ cik: '', exchange: 'NYSE', ticker: '', name: '', shares: 0, cusip: '' });
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ cik: '', exchange: 'NYSE', ticker: '', name: '', shares: 0, cusip: '' });
  const [lookupTicker, setLookupTicker] = useState('');
  const [lookupMsg, setLookupMsg] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);

  const load = (q?: string) => {
    setLoading(true);
    configApi.stocklists(q || search).then(r => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  const doSearch = () => { if (search.trim()) load(search.trim()); };

  const handleLookup = async () => {
    if (!lookupTicker.trim()) return;
    setLookupLoading(true);
    setLookupMsg('');
    try {
      const r = await configApi.lookupStocklist(lookupTicker.trim());
      const d = r.data;
      if (d.already_exists) {
        setLookupMsg(`"${d.ticker}" already exists in stocklists.`);
        setLookupLoading(false);
        return;
      }
      if (!d.found) {
        setLookupMsg(`"${lookupTicker.trim().toUpperCase()}" not found on SEC EDGAR.`);
        setLookupLoading(false);
        return;
      }
      setAddForm({
        ticker: d.ticker,
        exchange: d.exchange || 'NYSE',
        name: d.name || '',
        cik: d.cik || '',
        shares: d.shares || 0,
        cusip: d.cusip || '',
      });
      setShowAdd(true);
      setLookupMsg('');
    } catch {
      setLookupMsg('Lookup failed.');
    }
    setLookupLoading(false);
  };

  const startEdit = (item: StocklistItem) => {
    setEditId(item.id);
    setEditForm({ cik: item.cik, exchange: item.exchange, ticker: item.ticker, name: item.name, shares: item.shares, cusip: item.cusip });
  };
  const cancelEdit = () => { setEditId(null); };
  const saveEdit = async () => {
    if (editId === null) return;
    await configApi.updateStocklist(editId, editForm);
    cancelEdit();
    load();
  };
  const handleAdd = async () => {
    if (!addForm.ticker.trim() || !addForm.name.trim()) return;
    await configApi.createStocklist(addForm);
    setAddForm({ cik: '', exchange: 'NYSE', ticker: '', name: '', shares: 0, cusip: '' });
    setShowAdd(false);
    setLookupTicker('');
    load(addForm.ticker.trim());
  };
  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this stock?')) return;
    await configApi.deleteStocklist(id);
    load();
  };

  return (
    <>
      <div style={{ marginBottom: '0.5em', display: 'flex', gap: '0.5em', alignItems: 'center', flexWrap: 'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && doSearch()}
          placeholder="Search by ticker or name..." style={{ ...styles.input, width: '20em' }} />
        <input type="button" value="Search" onClick={doSearch} style={styles.button} />
      </div>
      <div style={{ marginBottom: '0.5em', display: 'flex', gap: '0.5em', alignItems: 'center', flexWrap: 'wrap' }}>
        <input value={lookupTicker} onChange={e => setLookupTicker(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleLookup()}
          placeholder="Ticker to lookup..." style={{ ...styles.input, width: '10em' }} />
        <input type="button" value={lookupLoading ? 'Looking up...' : 'Lookup'} onClick={handleLookup}
          disabled={lookupLoading} style={styles.button} />
        <input type="button" value="Add Manually" onClick={() => { setShowAdd(true); setLookupMsg(''); }} style={styles.button} />
        {lookupMsg && <span style={{ color: lookupMsg.includes('already') || lookupMsg.includes('not found') ? '#c00' : '#060', fontSize: '0.85em', fontWeight: 'bold' }}>{lookupMsg}</span>}
      </div>
      {showAdd && (
        <div style={styles.addRow}>
          <label style={styles.addLabel}>Ticker:</label>
          <input value={addForm.ticker} onChange={e => setAddForm({ ...addForm, ticker: e.target.value })} style={{ ...styles.input, width: '6em' }} autoFocus />
          <label style={{ ...styles.addLabel, marginLeft: '0.5em' }}>Exchange:</label>
          <select value={addForm.exchange} onChange={e => setAddForm({ ...addForm, exchange: e.target.value })} style={{ ...styles.input, width: '10em' }}>
            {EXCHANGES.map(ex => <option key={ex} value={ex}>{ex}</option>)}
          </select>
          <label style={{ ...styles.addLabel, marginLeft: '0.5em' }}>Name:</label>
          <input value={addForm.name} onChange={e => setAddForm({ ...addForm, name: e.target.value })} style={{ ...styles.input, width: '18em' }} />
          <label style={{ ...styles.addLabel, marginLeft: '0.5em' }}>CIK:</label>
          <input value={addForm.cik} onChange={e => setAddForm({ ...addForm, cik: e.target.value })} style={{ ...styles.input, width: '10em' }} />
          <label style={{ ...styles.addLabel, marginLeft: '0.5em' }}>Shares:</label>
          <input type="number" value={addForm.shares} onChange={e => setAddForm({ ...addForm, shares: parseInt(e.target.value) || 0 })} style={{ ...styles.input, width: '10em' }} />
          <label style={{ ...styles.addLabel, marginLeft: '0.5em' }}>CUSIP:</label>
          <input value={addForm.cusip} onChange={e => setAddForm({ ...addForm, cusip: e.target.value })} style={{ ...styles.input, width: '8em' }} />
          <input type="button" value="Save" onClick={handleAdd} style={{ ...styles.button, marginLeft: '0.5em' }} />
          <input type="button" value="Cancel" onClick={() => { setShowAdd(false); setAddForm({ cik: '', exchange: 'NYSE', ticker: '', name: '', shares: 0, cusip: '' }); }} style={{ ...styles.button, marginLeft: '4px' }} />
        </div>
      )}
      {loading ? <div style={{ textAlign: 'center', padding: '2em' }}>Loading...</div> : items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2em', color: '#666' }}>Search for stocks by ticker or name to view/edit</div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>ID</th>
              <th style={styles.th}>Ticker</th>
              <th style={styles.th}>Exchange</th>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>CIK</th>
              <th style={styles.th}>Shares Outstanding</th>
              <th style={styles.th}>CUSIP</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s, idx) => (
              <tr key={s.id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#e6f2ff' }}>
                <td style={{ ...styles.td, textAlign: 'center', width: '3em' }}>{s.id}</td>
                <td style={styles.td}>
                  {editId === s.id ? (
                    <input value={editForm.ticker} onChange={e => setEditForm({ ...editForm, ticker: e.target.value })} style={{ ...styles.input, width: '100%' }} autoFocus />
                  ) : s.ticker}
                </td>
                <td style={styles.td}>
                  {editId === s.id ? (
                    <select value={editForm.exchange} onChange={e => setEditForm({ ...editForm, exchange: e.target.value })} style={{ ...styles.input, width: '100%' }}>
                      {EXCHANGES.map(ex => <option key={ex} value={ex}>{ex}</option>)}
                    </select>
                  ) : s.exchange}
                </td>
                <td style={styles.td}>
                  {editId === s.id ? (
                    <input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} style={{ ...styles.input, width: '100%' }} />
                  ) : s.name}
                </td>
                <td style={styles.td}>
                  {editId === s.id ? (
                    <input value={editForm.cik} onChange={e => setEditForm({ ...editForm, cik: e.target.value })} style={{ ...styles.input, width: '100%' }} />
                  ) : s.cik}
                </td>
                <td style={{ ...styles.td, textAlign: 'right' }}>
                  {editId === s.id ? (
                    <input type="number" value={editForm.shares} onChange={e => setEditForm({ ...editForm, shares: parseInt(e.target.value) || 0 })} style={{ ...styles.input, width: '100%' }} />
                  ) : s.shares.toLocaleString()}
                </td>
                <td style={styles.td}>
                  {editId === s.id ? (
                    <input value={editForm.cusip} onChange={e => setEditForm({ ...editForm, cusip: e.target.value })} style={{ ...styles.input, width: '100%' }} />
                  ) : s.cusip}
                </td>
                <td style={{ ...styles.td, textAlign: 'center', width: '10em' }}>
                  {editId === s.id ? (
                    <>
                      <a href="#s" onClick={e => { e.preventDefault(); saveEdit(); }} style={styles.actionLink}>Save</a>
                      {' | '}
                      <a href="#c" onClick={e => { e.preventDefault(); cancelEdit(); }} style={styles.actionLink}>Cancel</a>
                    </>
                  ) : (
                    <>
                      <a href="#e" onClick={e => { e.preventDefault(); startEdit(s); }} style={styles.actionLink}>Edit</a>
                      {' | '}
                      <a href="#d" onClick={e => { e.preventDefault(); handleDelete(s.id); }} style={styles.actionLink}>Delete</a>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
};

/* ---- Missing Stocks Tab ---- */
const MissingStocksTab: React.FC = () => {
  const [items, setItems] = useState<MissingStock[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [exchange, setExchange] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);
  const [addResult, setAddResult] = useState('');
  const [progress, setProgress] = useState({ current: 0, total: 0, ticker: '' });
  const [addLog, setAddLog] = useState<{ ticker: string; status: string; shares: number; cusip: string }[]>([]);

  const load = async (ex?: string, s?: string) => {
    setLoading(true);
    setAddResult('');
    try {
      const r = await configApi.missingStocklists({ exchange: ex ?? exchange, search: s ?? search });
      setItems(r.data.items);
      setTotal(r.data.total);
      setSelected(new Set());
      setLoaded(true);
    } catch { }
    setLoading(false);
  };

  const toggleSelect = (ticker: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(ticker)) next.delete(ticker); else next.add(ticker);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === items.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map(i => i.ticker)));
    }
  };

  const handleAdd = async () => {
    const toAdd = items.filter(i => selected.has(i.ticker));
    if (toAdd.length === 0) return;
    if (!window.confirm(`Add ${toAdd.length} stock(s) to stocklists?\nShares & CUSIP will be looked up for each (~1-2s per stock).`)) return;
    setAdding(true);
    setAddResult('');
    setProgress({ current: 0, total: toAdd.length, ticker: '' });
    setAddLog([]);

    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`/api/configuration/stocklists/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(toAdd),
      });

      const reader = resp.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const evt = JSON.parse(line.slice(6));
                if (evt.type === 'progress') {
                  setProgress({ current: evt.current, total: evt.total, ticker: evt.ticker });
                  if (evt.status === 'added') {
                    setAddLog(prev => [...prev, { ticker: evt.ticker, status: evt.status, shares: evt.shares || 0, cusip: evt.cusip || '' }]);
                  }
                } else if (evt.type === 'done') {
                  setAddResult(`Done! Added ${evt.added} of ${evt.total} stock(s).`);
                }
              } catch { }
            }
          }
        }
      }
      await load();
    } catch {
      setAddResult('Failed to add stocks.');
    }
    setAdding(false);
  };

  return (
    <>
      <div style={{ marginBottom: '0.5em', display: 'flex', gap: '0.5em', alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ fontWeight: 'bold', fontSize: '0.85em' }}>Exchange:</label>
        <select value={exchange} onChange={e => setExchange(e.target.value)} style={{ ...styles.input, width: '10em' }}>
          <option value="">All</option>
          <option value="NYSE">NYSE</option>
          <option value="NASDAQ">NASDAQ</option>
        </select>
        <input value={search} onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && load()}
          placeholder="Filter by ticker or name..." style={{ ...styles.input, width: '18em' }} />
        <input type="button" value={loading ? 'Loading...' : 'Find Missing Stocks'}
          onClick={() => load()} disabled={loading} style={styles.button} />
        {loaded && selected.size > 0 && !adding && (
          <input type="button"
            value={`Add Selected (${selected.size})`}
            onClick={handleAdd}
            style={{ ...styles.button, backgroundColor: '#b3d9ff', fontWeight: 'bold' }} />
        )}
      </div>
      {adding && (
        <div style={{ backgroundColor: '#f0f7ff', border: '1px solid #0d58e6', padding: '8px 12px', marginBottom: '0.5em', fontSize: '0.85em' }}>
          <strong>Adding stocks...</strong> {progress.current}/{progress.total}
          {progress.ticker && <> — looking up <strong>{progress.ticker}</strong></>}
          <div style={{ marginTop: '4px', backgroundColor: '#ddd', height: '8px', borderRadius: '4px' }}>
            <div style={{ backgroundColor: '#0d58e6', height: '8px', borderRadius: '4px', width: `${progress.total ? (progress.current / progress.total * 100) : 0}%`, transition: 'width 0.3s' }} />
          </div>
        </div>
      )}
      {addResult && (
        <div style={{ fontSize: '0.85em', marginBottom: '0.5em', padding: '6px 12px', backgroundColor: addResult.includes('Done') ? '#d4edda' : '#f8d7da', borderRadius: '4px', fontWeight: 'bold' }}>
          {addResult}
        </div>
      )}
      {addLog.length > 0 && (
        <details style={{ fontSize: '0.85em', marginBottom: '0.5em' }}>
          <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Results ({addLog.length} added) — click to expand</summary>
          <table style={{ ...styles.table, marginTop: '4px' }}>
            <thead>
              <tr>
                <th style={styles.th}>Ticker</th>
                <th style={styles.th}>Shares</th>
                <th style={styles.th}>CUSIP</th>
                <th style={styles.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {addLog.map((l, idx) => (
                <tr key={l.ticker} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#e6f2ff' }}>
                  <td style={{ ...styles.td, fontWeight: 'bold' }}>{l.ticker}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>{l.shares ? l.shares.toLocaleString() : <span style={{ color: '#c00' }}>missing</span>}</td>
                  <td style={styles.td}>{l.cusip || <span style={{ color: '#c00' }}>missing</span>}</td>
                  <td style={{ ...styles.td, color: l.shares && l.cusip ? '#060' : '#c60' }}>{l.shares && l.cusip ? 'complete' : 'partial'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
      {loaded && (
        <div style={{ fontSize: '0.85em', marginBottom: '0.5em', color: '#333' }}>
          Found <strong>{total}</strong> missing stock(s){total > 200 ? ' (showing first 200)' : ''}.
          {search || exchange ? ' Filtered.' : ' These are US-listed stocks (NYSE/NASDAQ) on SEC EDGAR not in your stocklists.'}
        </div>
      )}
      {loading ? <div style={{ textAlign: 'center', padding: '2em' }}>Loading from SEC EDGAR...</div> : items.length > 0 && (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, width: '3em' }}>
                <input type="checkbox" checked={selected.size === items.length && items.length > 0}
                  onChange={selectAll} />
              </th>
              <th style={styles.th}>Ticker</th>
              <th style={styles.th}>Exchange</th>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>CIK</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s, idx) => (
              <tr key={s.ticker} style={{ backgroundColor: selected.has(s.ticker) ? '#d4edda' : idx % 2 === 0 ? '#fff' : '#e6f2ff' }}>
                <td style={{ ...styles.td, textAlign: 'center' }}>
                  <input type="checkbox" checked={selected.has(s.ticker)}
                    onChange={() => toggleSelect(s.ticker)} />
                </td>
                <td style={{ ...styles.td, fontWeight: 'bold' }}>{s.ticker}</td>
                <td style={styles.td}>{s.exchange}</td>
                <td style={styles.td}>{s.name}</td>
                <td style={styles.td}>{s.cik}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
};

/* ---- Missing Canadian Stocks Tab ---- */
const CA_EXCHANGES = ['TSE', 'CVE', 'CSE'];

const MissingCanadianStocksTab: React.FC = () => {
  const [items, setItems] = useState<MissingCanadian[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [exchange, setExchange] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [adding, setAdding] = useState(false);
  const [addResult, setAddResult] = useState('');
  const [progress, setProgress] = useState({ current: 0, total: 0, ticker: '' });
  const [addLog, setAddLog] = useState<{ ticker: string; status: string }[]>([]);

  const load = async (ex?: string, s?: string) => {
    setLoading(true);
    setAddResult('');
    try {
      const r = await configApi.missingStockSymbols({ exchange: ex ?? exchange, search: s ?? search });
      setItems(r.data.items);
      setTotal(r.data.total);
      setSelected(new Set());
      setLoaded(true);
    } catch { }
    setLoading(false);
  };

  const toggleSelect = (key: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === items.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map(i => `${i.exchange}:${i.ticker}`)));
    }
  };

  const handleAdd = async () => {
    const toAdd = items.filter(i => selected.has(`${i.exchange}:${i.ticker}`));
    if (toAdd.length === 0) return;
    if (!window.confirm(`Add ${toAdd.length} Canadian stock(s) to stock_symbols?`)) return;
    setAdding(true);
    setAddResult('');
    setProgress({ current: 0, total: toAdd.length, ticker: '' });
    setAddLog([]);

    try {
      const token = localStorage.getItem('token');
      const resp = await fetch(`/api/configuration/stock-symbols/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(toAdd),
      });

      const reader = resp.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const evt = JSON.parse(line.slice(6));
                if (evt.type === 'progress') {
                  setProgress({ current: evt.current, total: evt.total, ticker: evt.ticker });
                  if (evt.status === 'added') {
                    setAddLog(prev => [...prev, { ticker: evt.ticker, status: evt.status }]);
                  }
                } else if (evt.type === 'done') {
                  setAddResult(`Done! Added ${evt.added} of ${evt.total} stock(s).`);
                }
              } catch { }
            }
          }
        }
      }
      await load();
    } catch {
      setAddResult('Failed to add stocks.');
    }
    setAdding(false);
  };

  return (
    <>
      <div style={{ marginBottom: '0.5em', display: 'flex', gap: '0.5em', alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ fontWeight: 'bold', fontSize: '0.85em' }}>Exchange:</label>
        <select value={exchange} onChange={e => setExchange(e.target.value)} style={{ ...styles.input, width: '10em' }}>
          <option value="">All (TSE + CVE)</option>
          <option value="TSE">TSE (Toronto)</option>
          <option value="CVE">CVE (Venture)</option>
        </select>
        <input value={search} onChange={e => setSearch(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && load()}
          placeholder="Filter by ticker or name..." style={{ ...styles.input, width: '18em' }} />
        <input type="button" value={loading ? 'Loading...' : 'Find Missing Stocks'}
          onClick={() => load()} disabled={loading} style={styles.button} />
        {loaded && selected.size > 0 && !adding && (
          <input type="button"
            value={`Add Selected (${selected.size})`}
            onClick={handleAdd}
            style={{ ...styles.button, backgroundColor: '#b3d9ff', fontWeight: 'bold' }} />
        )}
      </div>
      <div style={{ fontSize: '0.8em', color: '#666', marginBottom: '0.5em' }}>
        Data source: TMX Company Directory (TSX + TSXV). CSE stocks are not available for auto-discovery — add manually via Stock Lists search.
      </div>
      {adding && (
        <div style={{ backgroundColor: '#f0f7ff', border: '1px solid #0d58e6', padding: '8px 12px', marginBottom: '0.5em', fontSize: '0.85em' }}>
          <strong>Adding stocks...</strong> {progress.current}/{progress.total}
          {progress.ticker && <> — adding <strong>{progress.ticker}</strong></>}
          <div style={{ marginTop: '4px', backgroundColor: '#ddd', height: '8px', borderRadius: '4px' }}>
            <div style={{ backgroundColor: '#0d58e6', height: '8px', borderRadius: '4px', width: `${progress.total ? (progress.current / progress.total * 100) : 0}%`, transition: 'width 0.3s' }} />
          </div>
        </div>
      )}
      {addResult && (
        <div style={{ fontSize: '0.85em', marginBottom: '0.5em', padding: '6px 12px', backgroundColor: addResult.includes('Done') ? '#d4edda' : '#f8d7da', borderRadius: '4px', fontWeight: 'bold' }}>
          {addResult}
        </div>
      )}
      {addLog.length > 0 && (
        <details style={{ fontSize: '0.85em', marginBottom: '0.5em' }}>
          <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Results ({addLog.length} added) — click to expand</summary>
          <table style={{ ...styles.table, marginTop: '4px' }}>
            <thead>
              <tr>
                <th style={styles.th}>Ticker</th>
                <th style={styles.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {addLog.map((l, idx) => (
                <tr key={l.ticker} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#e6f2ff' }}>
                  <td style={{ ...styles.td, fontWeight: 'bold' }}>{l.ticker}</td>
                  <td style={{ ...styles.td, color: '#060' }}>{l.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
      {loaded && (
        <div style={{ fontSize: '0.85em', marginBottom: '0.5em', color: '#333' }}>
          Found <strong>{total}</strong> missing stock(s){total > 200 ? ' (showing first 200)' : ''}.
          {search || exchange ? ' Filtered.' : ' These are TSX/TSXV-listed stocks not in your stock_symbols table.'}
        </div>
      )}
      {loading ? <div style={{ textAlign: 'center', padding: '2em' }}>Loading from TMX...</div> : items.length > 0 && (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, width: '3em' }}>
                <input type="checkbox" checked={selected.size === items.length && items.length > 0}
                  onChange={selectAll} />
              </th>
              <th style={styles.th}>Ticker</th>
              <th style={styles.th}>Exchange</th>
              <th style={styles.th}>Name</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s, idx) => {
              const key = `${s.exchange}:${s.ticker}`;
              return (
                <tr key={key} style={{ backgroundColor: selected.has(key) ? '#d4edda' : idx % 2 === 0 ? '#fff' : '#e6f2ff' }}>
                  <td style={{ ...styles.td, textAlign: 'center' }}>
                    <input type="checkbox" checked={selected.has(key)}
                      onChange={() => toggleSelect(key)} />
                  </td>
                  <td style={{ ...styles.td, fontWeight: 'bold' }}>{s.ticker}</td>
                  <td style={styles.td}>{s.exchange}</td>
                  <td style={styles.td}>{s.name}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
};

/* ---- Tracked 13F Filers Tab ---- */
const TrackedFilersTab: React.FC = () => {
  const [items, setItems] = useState<TrackedFilerItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({ cik: '', filerName: '', displayName: '', active: true });
  const [showAdd, setShowAdd] = useState(false);
  const [newForm, setNewForm] = useState({ cik: '', filerName: '', displayName: '', active: true });

  const load = () => {
    setLoading(true);
    configApi.trackedFilers().then(r => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const startEdit = (item: TrackedFilerItem) => {
    setEditId(item.id);
    setEditForm({ cik: item.cik, filerName: item.filerName, displayName: item.displayName, active: !!item.active });
  };
  const cancelEdit = () => { setEditId(null); };
  const saveEdit = async () => {
    if (editId === null) return;
    await configApi.updateTrackedFiler(editId, { cik: editForm.cik, filer_name: editForm.filerName, display_name: editForm.displayName, active: editForm.active });
    cancelEdit();
    load();
  };
  const handleAdd = async () => {
    if (!newForm.cik.trim() || !newForm.displayName.trim()) return;
    await configApi.createTrackedFiler({ cik: newForm.cik.trim(), filer_name: newForm.filerName.trim(), display_name: newForm.displayName.trim(), active: newForm.active });
    setNewForm({ cik: '', filerName: '', displayName: '', active: true });
    setShowAdd(false);
    load();
  };
  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this filer?')) return;
    await configApi.deleteTrackedFiler(id);
    load();
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '2em' }}>Loading...</div>;

  return (
    <>
      <div style={{ marginBottom: '0.5em' }}>
        <input type="button" value="Add Filer" onClick={() => setShowAdd(true)} style={styles.button} />
      </div>
      {showAdd && (
        <div style={styles.addRow}>
          <label style={styles.addLabel}>CIK:</label>
          <input value={newForm.cik} onChange={e => setNewForm({ ...newForm, cik: e.target.value })}
            style={{ ...styles.input, width: '10em' }} placeholder="0001067983" autoFocus />
          <label style={styles.addLabel}>Filer Name:</label>
          <input value={newForm.filerName} onChange={e => setNewForm({ ...newForm, filerName: e.target.value })}
            style={{ ...styles.input, width: '20em' }} placeholder="BERKSHIRE HATHAWAY INC" />
          <label style={styles.addLabel}>Display Name:</label>
          <input value={newForm.displayName} onChange={e => setNewForm({ ...newForm, displayName: e.target.value })}
            style={{ ...styles.input, width: '12em' }} placeholder="Berkshire Hathaway" />
          <label style={{ ...styles.addLabel, display: 'flex', alignItems: 'center', gap: '3px' }}>
            <input type="checkbox" checked={newForm.active} onChange={e => setNewForm({ ...newForm, active: e.target.checked })} /> Active
          </label>
          <input type="button" value="Save" onClick={handleAdd} style={{ ...styles.button, marginLeft: '0.5em' }} />
          <input type="button" value="Cancel" onClick={() => { setShowAdd(false); setNewForm({ cik: '', filerName: '', displayName: '', active: true }); }} style={{ ...styles.button, marginLeft: '4px' }} />
        </div>
      )}
      <table style={{ ...styles.table, width: 'auto' }}>
        <thead>
          <tr>
            <th style={styles.th}>ID</th>
            <th style={styles.th}>CIK</th>
            <th style={styles.th}>Filer Name</th>
            <th style={styles.th}>Display Name</th>
            <th style={styles.th}>Active</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r, idx) => (
            <tr key={r.id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#e6f2ff', opacity: r.active ? 1 : 0.5 }}>
              <td style={{ ...styles.td, textAlign: 'center', width: '3em' }}>{r.id}</td>
              {editId === r.id ? (
                <>
                  <td style={styles.td}><input value={editForm.cik} onChange={e => setEditForm({ ...editForm, cik: e.target.value })} style={{ ...styles.input, width: '10em' }} /></td>
                  <td style={styles.td}><input value={editForm.filerName} onChange={e => setEditForm({ ...editForm, filerName: e.target.value })} style={{ ...styles.input, width: '20em' }} /></td>
                  <td style={styles.td}><input value={editForm.displayName} onChange={e => setEditForm({ ...editForm, displayName: e.target.value })} style={{ ...styles.input, width: '12em' }} /></td>
                  <td style={{ ...styles.td, textAlign: 'center' }}><input type="checkbox" checked={editForm.active} onChange={e => setEditForm({ ...editForm, active: e.target.checked })} /></td>
                  <td style={styles.td}>
                    <input type="button" value="Save" onClick={saveEdit} style={styles.button} />
                    <input type="button" value="Cancel" onClick={cancelEdit} style={{ ...styles.button, marginLeft: '4px' }} />
                  </td>
                </>
              ) : (
                <>
                  <td style={styles.td}>{r.cik}</td>
                  <td style={styles.td}>{r.filerName}</td>
                  <td style={styles.td}><strong>{r.displayName}</strong></td>
                  <td style={{ ...styles.td, textAlign: 'center' }}>{r.active ? 'Y' : 'N'}</td>
                  <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                    <input type="button" value="Edit" onClick={() => startEdit(r)} style={styles.button} />
                    <input type="button" value="Delete" onClick={() => handleDelete(r.id)} style={{ ...styles.button, marginLeft: '4px' }} />
                  </td>
                </>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

/* ---- Running Average Cost Calculator ---- */
const TT_BUY = 0, TT_SELL = 1, TT_DIV = 2, TT_SPLIT = 3, TT_RSPLIT = 4, TT_XOUT = 5, TT_XIN = 6, TT_ROC = 7;
const TRAN_TYPES: { [key: number]: string } = { [TT_BUY]: 'Buy', [TT_SELL]: 'Sell', [TT_DIV]: 'Dividend', [TT_SPLIT]: 'Split', [TT_RSPLIT]: 'Rev Split', [TT_XOUT]: 'Xfer Out', [TT_XIN]: 'Xfer In', [TT_ROC]: 'ROC' };

interface AvgCostRow {
  id: number; date: string; account: string; ticker: string; type: number;
  shares: number; price: number; commission: number;
  runningShares: number; runningCost: number; avgCost: number;
  realizedGain: number | null; amount: number | null; notes: string | null;
}
interface AvgCostResult {
  ticker: string; rows: AvgCostRow[];
  totalShares: number; totalCost: number; avgCost: number;
  totalRealizedGain: number; totalDividend: number;
}

const AvgCostTab: React.FC = () => {
  const [ticker, setTicker] = useState('');
  const [account, setAccount] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [hideClosed, setHideClosed] = useState(false);
  const [upToLastBuy, setUpToLastBuy] = useState(false);
  const [accounts, setAccounts] = useState<{ id: number; account: string }[]>([]);
  const [data, setData] = useState<AvgCostResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    accountApi.list().then((res) => setAccounts(res.data)).catch(() => {});
  }, []);

  const handleSearch = () => {
    if (!ticker.trim()) return;
    setLoading(true);
    setError('');
    setData(null);
    transactionApi.avgCost(ticker.trim().toUpperCase(), account || undefined, fromDate || undefined)
      .then((res) => setData(res.data))
      .catch(() => setError('Failed to load data'))
      .finally(() => setLoading(false));
  };

  const typeColor = (t: number) => (t === TT_BUY || t === TT_XIN) ? 'green' : (t === TT_SELL || t === TT_XOUT) ? 'red' : '#333';
  const multiTicker = data ? data.ticker.includes('&') : false;

  const displayRows = (() => {
    if (!data) return [];
    let filtered = data.rows.filter((r) => r.type !== 2);
    if (hideClosed) {
      const lastZeroByTicker: { [tk: string]: number } = {};
      for (let i = 0; i < filtered.length; i++) {
        const r = filtered[i];
        if (r.runningShares !== 0) continue;
        if (r.type === TT_XOUT) continue;
        const next = filtered.slice(i + 1).find((n) => n.ticker === r.ticker);
        if (!next) continue;
        lastZeroByTicker[r.ticker] = i;
      }
      if (Object.keys(lastZeroByTicker).length > 0) {
        const closedIndices = new Set<number>();
        for (const tk of Object.keys(lastZeroByTicker)) {
          for (let i = 0; i <= lastZeroByTicker[tk]; i++) {
            if (filtered[i].ticker === tk) closedIndices.add(i);
          }
        }
        filtered = filtered.filter((_, i) => !closedIndices.has(i));
      }
    }
    if (upToLastBuy) {
      let lastBuyIdx = -1;
      for (let i = 0; i < filtered.length; i++) {
        if (filtered[i].type === TT_BUY || filtered[i].type === TT_XIN) lastBuyIdx = i;
      }
      if (lastBuyIdx >= 0) filtered = filtered.slice(0, lastBuyIdx + 1);
    }
    return filtered;
  })();

  const downloadXlsx = () => {
    if (!displayRows.length) return;
    const headers = ['#', 'Date', 'Acct', ...(multiTicker ? ['Ticker'] : []), 'Type', 'Shares', 'Price', 'Amount', 'Comm', 'Run Shares', 'Run Cost', 'Avg Cost', 'Notes'];
    const rows = displayRows.map((r, idx) => {
      const acctShort = r.account.includes('_') ? r.account.split('_').slice(1).join('_') : r.account;
      const amt = r.amount != null ? r.amount : r.shares * r.price;
      return [
        idx + 1, r.date, acctShort, ...(multiTicker ? [r.ticker] : []),
        TRAN_TYPES[r.type] || r.type, r.shares, Number(r.price.toFixed(6)),
        Number(amt.toFixed(2)), Number(r.commission.toFixed(2)), r.runningShares,
        Number(r.runningCost.toFixed(2)), Number(r.avgCost.toFixed(6)),
        r.notes || '',
      ];
    });
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Avg Cost');
    XLSX.writeFile(wb, `avg_cost_${data!.ticker.replace(/&/g, '_')}.xlsx`);
  };

  return (
    <>
      <div style={styles.addRow}>
        <span style={styles.addLabel}>Account:</span>
        <select value={account} onChange={(e) => setAccount(e.target.value)} style={{ ...styles.input, marginRight: '1em' }}>
          <option value="">All Accounts</option>
          {accounts.map((a) => <option key={a.id} value={a.account}>{a.account}</option>)}
        </select>
        <span style={styles.addLabel}>From:</span>
        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
          style={{ ...styles.input, width: '10em', marginRight: '1em' }} />
        <span style={styles.addLabel}>Ticker:</span>
        <input value={ticker} onChange={(e) => setTicker(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          style={{ ...styles.input, width: '14em' }} placeholder="e.g. AAPL or A&B&C" />
        <button onClick={handleSearch} style={styles.button}>Calculate</button>
        <label style={{ marginLeft: '1em', fontSize: '0.85em' }}>
          <input type="checkbox" checked={hideClosed} onChange={(e) => setHideClosed(e.target.checked)} style={{ marginRight: '4px' }} />
          Hide closed
        </label>
        <label style={{ marginLeft: '1em', fontSize: '0.85em' }}>
          <input type="checkbox" checked={upToLastBuy} onChange={(e) => setUpToLastBuy(e.target.checked)} style={{ marginRight: '4px' }} />
          Up to last buy
        </label>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: '2em' }}>Loading...</div>}
      {error && <div style={{ textAlign: 'center', padding: '1em', color: 'red' }}>{error}</div>}

      {data && (
        <>
          <div style={{ padding: '8px 12px', fontSize: '0.85em', backgroundColor: '#f0f8ff', border: '1px solid #0d58e6', marginBottom: '0.5em' }}>
            <strong>{data.ticker}</strong>
            {' — '}
            Shares: <strong>{data.totalShares}</strong>
            {' | '}
            Total Cost: <strong>${data.totalCost.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
            {' | '}
            Avg Cost: <strong>${data.avgCost.toFixed(6)}</strong>
            {' | '}
            Realized Gain: <strong style={{ color: data.totalRealizedGain >= 0 ? 'green' : 'red' }}>${data.totalRealizedGain.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
            {' | '}
            Dividends: <strong>${data.totalDividend.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
            {' | '}
            <button onClick={downloadXlsx} style={{ ...styles.button, padding: '1px 8px', fontSize: '0.85em' }}>Excel</button>
          </div>

          <table style={{ ...styles.table, width: 'auto' }}>
            <thead>
              <tr>
                <th style={{ ...styles.th, padding: '2px 4px' }}>#</th>
                <th style={{ ...styles.th, padding: '2px 4px' }}>Date</th>
                <th style={{ ...styles.th, padding: '2px 4px' }}>Acct</th>
                {multiTicker && <th style={{ ...styles.th, padding: '2px 4px' }}>Ticker</th>}
                <th style={{ ...styles.th, padding: '2px 4px' }}>Type</th>
                <th style={{ ...styles.th, padding: '2px 4px' }}>Shares</th>
                <th style={{ ...styles.th, padding: '2px 4px' }}>Price</th>
                <th style={{ ...styles.th, padding: '2px 4px' }}>Amount</th>
                <th style={{ ...styles.th, padding: '2px 4px' }}>Comm</th>
                <th style={{ ...styles.th, padding: '2px 4px' }}>Run Shares</th>
                <th style={{ ...styles.th, padding: '2px 4px' }}>Run Cost</th>
                <th style={{ ...styles.th, padding: '2px 4px' }}>Avg Cost</th>
                <th style={{ ...styles.th, padding: '2px 4px' }}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((r, idx) => {
                const acctShort = r.account.includes('_') ? r.account.split('_').slice(1).join('_') : r.account;
                const compact: React.CSSProperties = { ...styles.td, padding: '1px 4px', whiteSpace: 'nowrap' };
                return (
                  <tr key={r.id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#e6f2ff' }}>
                    <td style={{ ...compact, textAlign: 'center' }}>{idx + 1}{r.notes && <span title={r.notes} style={{ color: '#0d58e6', cursor: 'help', marginLeft: '2px' }}>*</span>}</td>
                    <td style={compact}>{r.date}</td>
                    <td style={compact}>{acctShort}</td>
                    {multiTicker && <td style={{ ...compact, fontWeight: 'bold' }}>{r.ticker}</td>}
                    <td style={{ ...compact, color: typeColor(r.type), fontWeight: 'bold' }}>{TRAN_TYPES[r.type] || r.type}</td>
                    <td style={{ ...compact, textAlign: 'right' }}>{r.shares}</td>
                    <td style={{ ...compact, textAlign: 'right' }}>{r.price.toFixed(6)}</td>
                    <td style={{ ...compact, textAlign: 'right' }}>{(r.amount != null ? r.amount : r.shares * r.price).toFixed(2)}</td>
                    <td style={{ ...compact, textAlign: 'right' }}>{r.commission.toFixed(2)}</td>
                    <td style={{ ...compact, textAlign: 'right', fontWeight: 'bold' }}>{r.runningShares}</td>
                    <td style={{ ...compact, textAlign: 'right' }}>{r.runningCost.toFixed(2)}</td>
                    <td style={{ ...compact, textAlign: 'right', fontWeight: 'bold' }}>{r.avgCost.toFixed(6)}</td>
                    <td style={{ ...compact }}>{r.notes || ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </>
  );
};

/* ---- Avg Sell Price Calculator ---- */

interface AvgSellRow {
  id: number; date: string; account: string;
  shares: number; price: number; amount: number; commission: number;
  proceeds: number; runningShares: number; runningProceeds: number; runningAvgSell: number;
}
interface AvgSellResult {
  ticker: string; rows: AvgSellRow[];
  totalShares: number; totalProceeds: number; totalCommission: number;
  avgSellPrice: number; avgSellNet: number;
}

const AvgSellTab: React.FC = () => {
  const [ticker, setTicker] = useState('');
  const [account, setAccount] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [accounts, setAccounts] = useState<{ id: number; account: string }[]>([]);
  const [data, setData] = useState<AvgSellResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    accountApi.list().then((res) => setAccounts(res.data)).catch(() => {});
  }, []);

  const handleSearch = () => {
    if (!ticker.trim()) return;
    setLoading(true);
    setError('');
    setData(null);
    transactionApi.avgSell(ticker.trim().toUpperCase(), account || undefined, fromDate || undefined)
      .then((res) => setData(res.data))
      .catch(() => setError('Failed to load data'))
      .finally(() => setLoading(false));
  };

  return (
    <>
      <div style={styles.addRow}>
        <span style={styles.addLabel}>Account:</span>
        <select value={account} onChange={(e) => setAccount(e.target.value)} style={{ ...styles.input, marginRight: '1em' }}>
          <option value="">All Accounts</option>
          {accounts.map((a) => <option key={a.id} value={a.account}>{a.account}</option>)}
        </select>
        <span style={styles.addLabel}>Ticker:</span>
        <input value={ticker} onChange={(e) => setTicker(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          style={{ ...styles.input, width: '8em' }} placeholder="e.g. AAPL" />
        <span style={styles.addLabel}>From:</span>
        <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
          style={{ ...styles.input, width: '10em', marginRight: '1em' }} />
        <button onClick={handleSearch} style={styles.button}>Calculate</button>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: '2em' }}>Loading...</div>}
      {error && <div style={{ textAlign: 'center', padding: '1em', color: 'red' }}>{error}</div>}

      {data && (
        <>
          <div style={{ padding: '8px 12px', fontSize: '0.85em', backgroundColor: '#fff0f0', border: '1px solid #ef5350', marginBottom: '0.5em' }}>
            <strong>{data.ticker}</strong>
            {' — '}
            Shares Sold: <strong>{data.totalShares}</strong>
            {' | '}
            Avg Sell Price: <strong>${data.avgSellPrice.toFixed(6)}</strong>
            {' | '}
            Avg Sell (net): <strong>${data.avgSellNet.toFixed(6)}</strong>
            {' | '}
            Total Proceeds: <strong>${data.totalProceeds.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
            {' | '}
            Total Commission: <strong>${data.totalCommission.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong>
          </div>

          <table style={{ ...styles.table, width: 'auto' }}>
            <thead>
              <tr>
                <th style={{ ...styles.th, padding: '2px 4px' }}>#</th>
                <th style={{ ...styles.th, padding: '2px 4px' }}>Date</th>
                <th style={{ ...styles.th, padding: '2px 4px' }}>Acct</th>
                <th style={{ ...styles.th, padding: '2px 4px' }}>Shares</th>
                <th style={{ ...styles.th, padding: '2px 4px' }}>Price</th>
                <th style={{ ...styles.th, padding: '2px 4px' }}>Amount</th>
                <th style={{ ...styles.th, padding: '2px 4px' }}>Comm</th>
                <th style={{ ...styles.th, padding: '2px 4px' }}>Proceeds</th>
                <th style={{ ...styles.th, padding: '2px 4px' }}>Run Shares</th>
                <th style={{ ...styles.th, padding: '2px 4px' }}>Run Proceeds</th>
                <th style={{ ...styles.th, padding: '2px 4px' }}>Avg Sell</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, idx) => {
                const acctShort = r.account.includes('_') ? r.account.split('_').slice(1).join('_') : r.account;
                const compact: React.CSSProperties = { ...styles.td, padding: '1px 4px', whiteSpace: 'nowrap' };
                return (
                  <tr key={r.id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#ffe6e6' }}>
                    <td style={{ ...compact, textAlign: 'center' }}>{idx + 1}</td>
                    <td style={compact}>{r.date}</td>
                    <td style={compact}>{acctShort}</td>
                    <td style={{ ...compact, textAlign: 'right' }}>{r.shares}</td>
                    <td style={{ ...compact, textAlign: 'right' }}>{r.price.toFixed(6)}</td>
                    <td style={{ ...compact, textAlign: 'right' }}>{r.amount.toFixed(2)}</td>
                    <td style={{ ...compact, textAlign: 'right' }}>{r.commission.toFixed(2)}</td>
                    <td style={{ ...compact, textAlign: 'right' }}>{r.proceeds.toFixed(2)}</td>
                    <td style={{ ...compact, textAlign: 'right', fontWeight: 'bold' }}>{r.runningShares}</td>
                    <td style={{ ...compact, textAlign: 'right' }}>{r.runningProceeds.toFixed(2)}</td>
                    <td style={{ ...compact, textAlign: 'right', fontWeight: 'bold' }}>{r.runningAvgSell.toFixed(6)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </>
  );
};

/* ---- Main Component ---- */
const ConfigurationPage: React.FC = () => {
  const [tab, setTab] = useState<TabKey>('reasons');

  return (
    <div style={styles.container}>
      <h4 style={styles.heading}>Configuration</h4>
      <div style={styles.tabs}>
        {([
          { key: 'reasons' as TabKey, label: 'Transaction Reasons' },
          { key: 'sectors' as TabKey, label: 'Sectors' },
          { key: 'industries' as TabKey, label: 'Industries' },
          { key: 'accounts' as TabKey, label: 'Accounts' },
          { key: 'stocklists' as TabKey, label: 'Stock Lists' },
          { key: 'missing' as TabKey, label: 'Missing American Stocks' },
          { key: 'missingCA' as TabKey, label: 'Missing Canadian Stocks' },
          { key: 'trackedFilers' as TabKey, label: '13F Filers' },
          { key: 'avgCost' as TabKey, label: 'Avg Cost Calculator' },
          { key: 'avgSell' as TabKey, label: 'Avg Sell Calculator' },
        ]).map((t) => (
          <span
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{ ...styles.tabItem, ...(tab === t.key ? styles.activeTab : {}) }}
          >
            {t.label}
          </span>
        ))}
      </div>

      {tab === 'reasons' && <ReasonsTab />}
      {tab === 'sectors' && <SectorsTab />}
      {tab === 'industries' && <IndustriesTab />}
      {tab === 'accounts' && <AccountsTab />}
      {tab === 'stocklists' && <StocklistsTab />}
      {tab === 'missing' && <MissingStocksTab />}
      {tab === 'missingCA' && <MissingCanadianStocksTab />}
      {tab === 'trackedFilers' && <TrackedFilersTab />}
      {tab === 'avgCost' && <AvgCostTab />}
      {tab === 'avgSell' && <AvgSellTab />}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { padding: '1em 0.5em', backgroundColor: '#fff', minHeight: 'calc(100vh - 6em)' },
  heading: { color: '#0069cc', marginTop: '1em', textAlign: 'center' },
  tabs: { display: 'flex', gap: '0', marginBottom: '1em', borderBottom: '2px solid #0d58e6' },
  tabItem: { padding: '6px 16px', cursor: 'pointer', fontSize: '0.85em', fontWeight: 'bold', backgroundColor: '#e6f2ff', border: '1px solid #0d58e6', borderBottom: 'none', marginRight: '2px' },
  activeTab: { backgroundColor: '#cce6ff', color: '#000' },
  addRow: { backgroundColor: '#ececec', padding: '8px 12px', fontSize: '0.85em', marginBottom: '0.5em', display: 'flex', alignItems: 'center', flexWrap: 'wrap' as const, gap: '4px' },
  addLabel: { fontWeight: 'bold' },
  table: { width: '100%', marginTop: '0.5em', border: '2px solid #0d58e6', borderCollapse: 'collapse' as const },
  th: { border: '1px solid #0d58e6', padding: '4px 6px', backgroundColor: '#cce6ff', fontWeight: 'bold', fontSize: '0.8em', textAlign: 'center' as const },
  td: { border: '1px solid #0d58e6', padding: '3px 6px', fontSize: '0.8em' },
  input: { padding: '2px 4px', fontFamily: 'Arial, sans-serif', fontSize: '1em' },
  button: { padding: '4px 16px', backgroundColor: '#ccdcfc', color: '#000', border: '1px solid #808080', cursor: 'pointer', fontFamily: 'Arial, sans-serif', fontSize: '1em' },
  actionLink: { color: '#0069cc', textDecoration: 'none', cursor: 'pointer', fontSize: '0.9em' },
};

export default ConfigurationPage;
